import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ThunkConfig, ErrorField } from "store";
import { cabinetService } from "services/cabinetService";
import {
  IClientProfileInfoResponse,
  IClientRequestsResponse,
  IChangePasswordRequest,
  IChangePasswordResponse,
} from "models/cabinet";

const handleUnauthorized = () => {
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = "/";
};

export const getClientProfileInfo = createAsyncThunk<
  IClientProfileInfoResponse,
  void,
  ThunkConfig<ErrorField[]>
>("cabinet/getClientProfileInfo", async (_, { rejectWithValue }) => {
  try {
    const clientId =
      localStorage.getItem("clientId") || sessionStorage.getItem("clientId");
    if (!clientId) {
      throw new Error("Client ID is null");
    }
    const response = await cabinetService.getClientProfileInfo();
    return response;
  } catch (e: any) {
    if (e.response?.status === 401) {
      handleUnauthorized();
      return rejectWithValue([{ field: 'auth', message: 'Unauthorized' }]);
    }
    return rejectWithValue(e.response.data.errors);
  }
});

export const getClientRequests = createAsyncThunk<
  IClientRequestsResponse,
  void,
  ThunkConfig<ErrorField[]>
>("cabinet/getClientRequests", async (_, { rejectWithValue }) => {
  try {
    const response = await cabinetService.getClientRequests();
    return response;
  } catch (e: any) {
    if (e.response?.status === 401) {
      handleUnauthorized();
      return rejectWithValue([{ field: 'auth', message: 'Unauthorized' }]);
    }
    return rejectWithValue(e.response.data.errors);
  }
});

export const changePassword = createAsyncThunk<
  IChangePasswordResponse,
  IChangePasswordRequest,
  ThunkConfig<ErrorField[]>
>("cabinet/changePassword", async (data, { rejectWithValue }) => {
  try {
    const response = await cabinetService.changePassword(data);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export interface CabinetSchema {
  clientId: string;
  email: string;
  firstName: string;
  lastName: string;
  country: string | null;
  kycStatus: string;
  orders?: IClientRequestsResponse;
  isOpenPopup?: boolean;
  companyName?: string;
  position?: string;
}
const initialState: CabinetSchema = {
  clientId: "",
  email: "",
  firstName: "",
  lastName: "",
  country: "",
  kycStatus: "",
};
export const cabinetSlice = createSlice({
  name: "cabinet",
  initialState,
  reducers: {
    setIsOpenPopup: (state, action) => {
      state.isOpenPopup = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getClientProfileInfo.fulfilled, (state, action) => {
      return { ...state, ...action.payload };
    });
    builder.addCase(getClientRequests.fulfilled, (state, action) => {
      return { ...state, orders: action.payload };
    });
    builder.addCase(changePassword.fulfilled, (state, action) => {
      if (action.payload.isChanged) {
        return { ...state, isOpenPopup: true };
      }
    });
  },
});

export const { actions: cabinetActions } = cabinetSlice;
export const { reducer: cabinetReducer } = cabinetSlice;
